import { useState } from 'react';
import {
  User,
  Bell,
  Globe,
  Palette,
  Save,
  Moon,
  Sun,
  Languages,
  Mail,
  Building,
  MapPin,
  Phone,
  Link2,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';

type SettingsTab = 'profile' | 'site' | 'notifications' | 'appearance';

interface SiteSettings {
  siteName: string;
  siteDescription: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  socialLinks: {
    twitter: string;
    linkedin: string;
    github: string;
  };
}

interface NotificationSettings {
  emailNotifications: boolean;
  newMessages: boolean;
  newProjects: boolean;
  weeklyReport: boolean;
  securityAlerts: boolean;
}

const tabs = [
  { id: 'profile' as const, label: 'Profil', icon: <User size={18} /> },
  { id: 'site' as const, label: 'Site Ayarlari', icon: <Globe size={18} /> },
  { id: 'notifications' as const, label: 'Bildirimler', icon: <Bell size={18} /> },
  { id: 'appearance' as const, label: 'Gorunum', icon: <Palette size={18} /> },
];

export function AdminSettings() {
  const { session } = useAdminAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [saved, setSaved] = useState(false);

  // Profile settings
  const [profile, setProfile] = useState({
    name: session?.user.name || 'Administrator',
    email: session?.user.email || 'admin@bosphoruslabs.com',
  });

  // Site settings
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    siteName: 'Bosphorus Labs',
    siteDescription: 'Dijital donusum ve yazilim cozumleri',
    contactEmail: 'info@bosphoruslabs.com',
    contactPhone: '+90 212 123 4567',
    address: 'Levent, Istanbul, Turkiye',
    socialLinks: {
      twitter: 'https://twitter.com/bosphoruslabs',
      linkedin: 'https://linkedin.com/company/bosphoruslabs',
      github: 'https://github.com/bosphoruslabs',
    },
  });

  // Notification settings
  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNotifications: true,
    newMessages: true,
    newProjects: true,
    weeklyReport: false,
    securityAlerts: true,
  });

  // Appearance settings
  const [appearance, setAppearance] = useState({
    theme: 'dark' as 'dark' | 'light' | 'system',
    language: 'tr' as 'tr' | 'en',
    compactMode: false,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Ayarlar</h1>
          <p className="text-text-secondary">Hesap ve sistem ayarlarini yonetin</p>
        </div>
        <button
          onClick={handleSave}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors",
            saved ? 'bg-green-500' : 'bg-accent hover:bg-accent/90'
          )}
        >
          {saved ? <><CheckCircle size={18} /> Kaydedildi</> : <><Save size={18} /> Kaydet</>}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-bg-card border border-border-default rounded-xl p-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                  activeTab === tab.id
                    ? 'bg-accent text-white'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                )}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-bg-card border border-border-default rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-text-primary">Profil Bilgileri</h2>

              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                  {profile.name.charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-text-primary">{profile.name}</p>
                  <p className="text-sm text-text-secondary">{session?.user.role.replace('_', ' ')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-text-tertiary mb-2 block">Ad Soyad</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-tertiary mb-2 block">E-posta</label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Site Tab */}
          {activeTab === 'site' && (
            <div className="bg-bg-card border border-border-default rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-text-primary">Site Ayarlari</h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-text-tertiary mb-2 block flex items-center gap-2">
                    <Building size={14} /> Site Adi
                  </label>
                  <input
                    type="text"
                    value={siteSettings.siteName}
                    onChange={(e) => setSiteSettings({ ...siteSettings, siteName: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-tertiary mb-2 block">Site Aciklamasi</label>
                  <textarea
                    value={siteSettings.siteDescription}
                    onChange={(e) => setSiteSettings({ ...siteSettings, siteDescription: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-text-tertiary mb-2 block flex items-center gap-2">
                      <Mail size={14} /> Iletisim E-posta
                    </label>
                    <input
                      type="email"
                      value={siteSettings.contactEmail}
                      onChange={(e) => setSiteSettings({ ...siteSettings, contactEmail: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-text-tertiary mb-2 block flex items-center gap-2">
                      <Phone size={14} /> Telefon
                    </label>
                    <input
                      type="tel"
                      value={siteSettings.contactPhone}
                      onChange={(e) => setSiteSettings({ ...siteSettings, contactPhone: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-text-tertiary mb-2 block flex items-center gap-2">
                    <MapPin size={14} /> Adres
                  </label>
                  <input
                    type="text"
                    value={siteSettings.address}
                    onChange={(e) => setSiteSettings({ ...siteSettings, address: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>

                <div className="pt-4 border-t border-border-default">
                  <h3 className="text-sm font-medium text-text-primary mb-4 flex items-center gap-2">
                    <Link2 size={14} /> Sosyal Medya Linkleri
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(siteSettings.socialLinks).map(([key, value]) => (
                      <div key={key}>
                        <label className="text-xs text-text-tertiary mb-1 block capitalize">{key}</label>
                        <input
                          type="url"
                          value={value}
                          onChange={(e) => setSiteSettings({
                            ...siteSettings,
                            socialLinks: { ...siteSettings.socialLinks, [key]: e.target.value }
                          })}
                          className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="bg-bg-card border border-border-default rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-text-primary">Bildirim Ayarlari</h2>

              <div className="space-y-4">
                {[
                  { key: 'emailNotifications', label: 'E-posta Bildirimleri', desc: 'Genel e-posta bildirimlerini ac/kapat' },
                  { key: 'newMessages', label: 'Yeni Mesajlar', desc: 'Iletisim formundan yeni mesaj geldiginde bildir' },
                  { key: 'newProjects', label: 'Yeni Proje Talepleri', desc: 'Yeni proje talebi geldiginde bildir' },
                  { key: 'weeklyReport', label: 'Haftalik Rapor', desc: 'Haftalik ozet raporu e-posta ile gonder' },
                  { key: 'securityAlerts', label: 'Guvenlik Uyarilari', desc: 'Onemli guvenlik olaylari icin bildir' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 rounded-lg bg-bg-elevated">
                    <div>
                      <p className="font-medium text-text-primary">{item.label}</p>
                      <p className="text-sm text-text-secondary">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => setNotifications({
                        ...notifications,
                        [item.key]: !notifications[item.key as keyof NotificationSettings]
                      })}
                      className={cn(
                        'relative w-12 h-6 rounded-full transition-colors',
                        notifications[item.key as keyof NotificationSettings] ? 'bg-accent' : 'bg-bg-card border border-border-default'
                      )}
                    >
                      <span className={cn(
                        'absolute top-1 w-4 h-4 rounded-full bg-white transition-all',
                        notifications[item.key as keyof NotificationSettings] ? 'left-7' : 'left-1'
                      )} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="bg-bg-card border border-border-default rounded-xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-text-primary">Gorunum Ayarlari</h2>

              <div className="space-y-6">
                {/* Theme */}
                <div>
                  <label className="text-sm text-text-tertiary mb-3 block">Tema</label>
                  <div className="flex gap-3">
                    {[
                      { value: 'light', icon: <Sun size={18} />, label: 'Acik' },
                      { value: 'dark', icon: <Moon size={18} />, label: 'Koyu' },
                      { value: 'system', icon: <Palette size={18} />, label: 'Sistem' },
                    ].map(theme => (
                      <button
                        key={theme.value}
                        onClick={() => setAppearance({ ...appearance, theme: theme.value as any })}
                        className={cn(
                          'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
                          appearance.theme === theme.value
                            ? 'bg-accent text-white border-accent'
                            : 'bg-bg-elevated border-border-default text-text-secondary hover:text-text-primary'
                        )}
                      >
                        {theme.icon}
                        {theme.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label className="text-sm text-text-tertiary mb-3 block flex items-center gap-2">
                    <Languages size={14} /> Dil
                  </label>
                  <select
                    value={appearance.language}
                    onChange={(e) => setAppearance({ ...appearance, language: e.target.value as any })}
                    className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="tr">Turkce</option>
                    <option value="en">English</option>
                  </select>
                </div>

                {/* Compact Mode */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-bg-elevated">
                  <div>
                    <p className="font-medium text-text-primary">Kompakt Mod</p>
                    <p className="text-sm text-text-secondary">Daha az bosluk ile daha fazla icerik gor</p>
                  </div>
                  <button
                    onClick={() => setAppearance({ ...appearance, compactMode: !appearance.compactMode })}
                    className={cn(
                      'relative w-12 h-6 rounded-full transition-colors',
                      appearance.compactMode ? 'bg-accent' : 'bg-bg-card border border-border-default'
                    )}
                  >
                    <span className={cn(
                      'absolute top-1 w-4 h-4 rounded-full bg-white transition-all',
                      appearance.compactMode ? 'left-7' : 'left-1'
                    )} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
